import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Box,
  Typography,
} from "@mui/material";
import { ApiURLs, imageBaseUrls } from "../../constant/ApiUrls";
import { fetchData, updateData } from "../../services/api";
import placeholderImage from "../../components/assets/image/placeholder.png";
import AddAttributes from "../category/components/AddAttributes";

// Inline SVG icon components (avoids @mui/icons-material dependency)
const FilterListIcon = ({ sx = {} }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: sx.fontSize || 24,
      color: sx.color || "inherit",
      mr: sx.mr || 0,
    }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
    </svg>
  </Box>
);

const ExpandMoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
  </svg>
);

const ExpandLessIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" />
  </svg>
);

export default function FilterManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [attributes, setAttributes] = useState({});
  const [attrLoading, setAttrLoading] = useState({});
  const [togglingId, setTogglingId] = useState(null);

  // Watch Redux attributeReducer for changes triggered by AddAttributes modal
  const reduxAttributeState = useSelector((state) => state.attributeReducer);
  const prevReduxRef = useRef(reduxAttributeState);

  // Fetch all top-level categories
  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${ApiURLs.CATEGORIES}?parent_id=0`;
      const res = await fetchData(url);
      if (res.status === 1) {
        setCategories(res.data.result || []);
      }
    } catch (err) {
      console.error("Failed to load categories:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Fetch attributes for a specific category
  const loadAttributes = useCallback(async (categoryId) => {
    setAttrLoading((prev) => ({ ...prev, [categoryId]: true }));
    try {
      const url = `${ApiURLs.ATTRIBUTE}?category_id=${categoryId}`;
      const res = await fetchData(url);
      if (res.status === 1) {
        setAttributes((prev) => ({
          ...prev,
          [categoryId]: res.data.result || [],
        }));
      }
    } catch (err) {
      console.error("Failed to load attributes:", err);
    } finally {
      setAttrLoading((prev) => ({ ...prev, [categoryId]: false }));
    }
  }, []);

  // Re-fetch attributes when Redux store updates (after AddAttributes modal submit)
  useEffect(() => {
    if (
      expandedCategory &&
      prevReduxRef.current.categoryListing !== reduxAttributeState.categoryListing
    ) {
      loadAttributes(expandedCategory);
    }
    prevReduxRef.current = reduxAttributeState;
  }, [reduxAttributeState.categoryListing, expandedCategory, loadAttributes]);

  // Toggle expanded category
  const handleToggleCategory = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(categoryId);
      // Load attributes if not already loaded
      if (!attributes[categoryId]) {
        loadAttributes(categoryId);
      }
    }
  };

  // Toggle is_filter for an attribute
  const handleToggleFilter = async (attribute, categoryId) => {
    const newFilterValue = attribute.is_filter === 1 ? 0 : 1;
    setTogglingId(attribute.id);

    try {
      const payload = {
        label: attribute.label,
        category_id: categoryId,
        input_type: attribute.input_type,
        is_filter: newFilterValue,
        type: attribute.type || "",
        is_single: attribute.is_single,
        options_value: attribute.options_value
          ? typeof attribute.options_value === "string"
            ? JSON.parse(attribute.options_value)
            : attribute.options_value
          : [],
      };

      await updateData(payload, `${ApiURLs.ATTRIBUTE}/${attribute.id}`);

      // Update local state immediately
      setAttributes((prev) => ({
        ...prev,
        [categoryId]: (prev[categoryId] || []).map((attr) =>
          attr.id === attribute.id
            ? { ...attr, is_filter: newFilterValue }
            : attr
        ),
      }));
    } catch (err) {
      console.error("Failed to toggle filter:", err);
    } finally {
      setTogglingId(null);
    }
  };

  // Refresh attributes after adding a new one
  const handleAttributeAdded = (categoryId) => {
    loadAttributes(categoryId);
  };

  // Count filter attributes per category
  const getFilterCount = (categoryId) => {
    const attrs = attributes[categoryId];
    if (!attrs) return null;
    return attrs.filter((a) => a.is_filter === 1).length;
  };

  if (loading) {
    return (
      <Paper
        sx={{
          width: "100%",
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", p: 2 }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "8px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        }}
      >
        <FilterListIcon sx={{ fontSize: 32, color: "#1976d2", mr: 2 }} />
        <div>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#333" }}>
            Filter Management
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
            Manage which attributes appear as filters on the frontend. Click a
            category to expand its attributes and toggle filter visibility.
          </Typography>
        </div>
      </Box>

      {/* Categories List */}
      {categories.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="textSecondary">
            No categories found.
          </Typography>
        </Box>
      ) : (
        categories.map((category) => {
          const isExpanded = expandedCategory === category.id;
          const catAttrs = attributes[category.id] || [];
          const isLoadingAttrs = attrLoading[category.id];
          const filterCount = getFilterCount(category.id);

          return (
            <Box
              key={category.id}
              sx={{
                mb: 2,
                border: "1px solid #ddd",
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: isExpanded
                  ? "0 4px 12px rgba(0,0,0,0.12)"
                  : "0 1px 4px rgba(0,0,0,0.06)",
                transition: "box-shadow 0.2s ease",
              }}
            >
              {/* Category Row */}
              <Box
                onClick={() => handleToggleCategory(category.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  backgroundColor: isExpanded ? "#f5f9ff" : "#fff",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f0f4fa" },
                  transition: "background-color 0.2s ease",
                }}
              >
                <img
                  src={
                    category.image
                      ? imageBaseUrls(category.image)
                      : placeholderImage
                  }
                  alt={category.name || "Category"}
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "8px",
                    objectFit: "cover",
                    marginRight: "15px",
                    border: "1px solid #eee",
                  }}
                />

                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", color: "#333" }}
                  >
                    {category.name || "Unnamed Category"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#888" }}>
                    {category.description || "No description"}
                  </Typography>
                </Box>

                {/* Filter count badge */}
                {filterCount !== null && (
                  <Chip
                    label={`${filterCount} filter${filterCount !== 1 ? "s" : ""} active`}
                    size="small"
                    color={filterCount > 0 ? "primary" : "default"}
                    variant={filterCount > 0 ? "filled" : "outlined"}
                    sx={{ mr: 2 }}
                  />
                )}

                <IconButton size="small">
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              {/* Expanded Attributes Panel */}
              <Collapse in={isExpanded} timeout="auto">
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#fafbfc",
                    borderTop: "1px solid #eee",
                  }}
                >
                  {isLoadingAttrs ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        py: 3,
                      }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  ) : catAttrs.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 2,
                      }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        No attributes found for this category.
                      </Typography>
                      <AddAttributes
                        parentId={category.id}
                        buttonLabel="Add Filter Attribute"
                      />
                    </Box>
                  ) : (
                    <>
                      {/* Action bar */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mb: 1,
                        }}
                      >
                        <AddAttributes
                          parentId={category.id}
                          buttonLabel="Add Attribute"
                        />
                      </Box>

                      {/* Attributes Table */}
                      <Table size="small" sx={{ minWidth: 600 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Attribute Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Input Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Options
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold" }}
                              align="center"
                            >
                              Show in Filters
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {catAttrs.map((attr, index) => (
                            <TableRow
                              key={attr.id}
                              sx={{
                                backgroundColor:
                                  attr.is_filter === 1
                                    ? "#e8f5e9"
                                    : "transparent",
                                "&:hover": { backgroundColor: "#f5f5f5" },
                                transition: "background-color 0.2s ease",
                              }}
                            >
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {attr.label}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={attr.input_type || "text"}
                                  size="small"
                                  variant="outlined"
                                  sx={{ textTransform: "capitalize" }}
                                />
                              </TableCell>
                              <TableCell>
                                {attr.input_type === "select"
                                  ? attr.type || "dropdown"
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                {attr.options_value
                                  ? (() => {
                                      try {
                                        const v = JSON.parse(
                                          attr.options_value
                                        );
                                        if (Array.isArray(v)) {
                                          const display = v.slice(0, 5);
                                          return (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 0.5,
                                              }}
                                            >
                                              {display.map((opt, i) => (
                                                <Chip
                                                  key={i}
                                                  label={opt}
                                                  size="small"
                                                  variant="outlined"
                                                  sx={{ fontSize: "11px" }}
                                                />
                                              ))}
                                              {v.length > 5 && (
                                                <Chip
                                                  label={`+${v.length - 5} more`}
                                                  size="small"
                                                  sx={{ fontSize: "11px" }}
                                                />
                                              )}
                                            </Box>
                                          );
                                        }
                                        return "-";
                                      } catch {
                                        return "-";
                                      }
                                    })()
                                  : "-"}
                              </TableCell>
                              <TableCell align="center">
                                {togglingId === attr.id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Switch
                                    checked={attr.is_filter === 1}
                                    onChange={() =>
                                      handleToggleFilter(attr, category.id)
                                    }
                                    color="success"
                                    size="small"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </Box>
              </Collapse>
            </Box>
          );
        })
      )}
    </Paper>
  );
}
